import { Button, Flex, H1, Input, Section, Space } from '@dnb/eufemia';
import { OrganizationDto, organizationInputSchema } from '@portals/shared/admin/OrganizationDto';
import { useEufemiaForm } from '@portals/shared-frontend/hooks';
import { type JSX, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';

import { saveOrganization } from '@/api/organizations';
import BackButton from '@/components/BackButton';
import Container from '@/components/Container';
import LoadingModal from '@/components/LoadingModal';
import LoadingPage from '@/components/LoadingPage';

export default function OrganizationForm(): JSX.Element {
  const navigate = useNavigate();
  const { organizationId } = useParams();

  const { data: organization, isValidating: organizationLoading } = useSWR<OrganizationDto>(
    organizationId && `/organizations/${organizationId}`,
  );

  const {
    controller: { setValues },
    controller,
    register,
    handleSubmit,
    submitting,
  } = useEufemiaForm(organizationInputSchema, {
    domain: null,
    ciamUatPartyId: null,
    ciamProdPartyId: null,
    tppId: null,
  });

  useEffect(() => {
    if (controller.values.domain === '') {
      controller.setValue('domain', null);
    }
    if (controller.values.ciamProdPartyId === '') {
      controller.setValue('ciamProdPartyId', null);
    }
    if (controller.values.ciamUatPartyId === '') {
      controller.setValue('ciamUatPartyId', null);
    }
    if (controller.values.tppId === '') {
      controller.setValue('tppId', null);
    }
  }, [controller]);

  useEffect(() => {
    if (organization) {
      setValues(organization);
    }
  }, [organization, setValues]);

  const onSubmit = handleSubmit(
    useCallback(
      async (data) => {
        await saveOrganization(data);
        navigate('..');
      },
      [navigate],
    ),
  );

  const title = organization ? 'Edit organization' : 'Add organization';

  if (organizationLoading) {
    return <LoadingPage />;
  }

  return (
    <>
      <BackButton to="/organizations">Organizations</BackButton>
      <Container centered size="small">
        {submitting && <LoadingModal />}

        <H1 bottom={false} top="large">
          {title}
        </H1>

        <form onSubmit={onSubmit}>
          <Section backgroundColor="white" innerSpace={{ block: 'x-large' }}>
            <Flex.Vertical>
              <Input label="Name" stretch {...register.input('name')} />
              {!organizationId && (
                <Space top="medium">
                  <Input label="Domain" stretch {...register.input('domain')} />
                </Space>
              )}
              <Space top="medium">
                <Input label="Ciam Uat Party Id" stretch {...register.input('ciamUatPartyId')} />
              </Space>
              <Space top="medium">
                <Input label="Ciam Prod Party Id" stretch {...register.input('ciamProdPartyId')} />
              </Space>
              <Space top="medium">
                <Input label="TPP Id" stretch {...register.input('tppId')} />
              </Space>
            </Flex.Vertical>
          </Section>
          <Section backgroundColor="divider" innerSpace={{ block: 'medium' }}>
            <Button disabled={submitting} icon="save" iconPosition="left" type="submit">
              Save
            </Button>
          </Section>
        </form>
      </Container>
    </>
  );
}
